import { useCallback } from "react"
import { History, X } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "../../components/button"
import { Composer } from "./composer"
import { SidePanelSlot } from "./side-panel-slot"
import { Transcript } from "./transcript"
import { useIsMobile } from "../../hooks/use-mobile"
import { useKeyboardInset } from "./hooks/use-keyboard-inset"
import type { AgentChatProps } from "./types"

/**
 * The top-level agent chat surface — transcript on top, composer
 * below, optional side panel adjacent. Headless: every piece of data
 * flows in via props. The host owns transport (SSE), agent
 * resolution, attachment storage, and persistence.
 *
 * For most apps prefer `<AgentChatDock>` which wraps this in a Sheet.
 * Use `<AgentChat>` directly only when embedding inline (e.g. a
 * dedicated /chat page).
 */
export function AgentChat({
  agent,
  agents,
  messages,
  status,
  attachments,
  onSendMessage,
  onStop,
  onSelectAgent,
  onAttachFiles,
  onPickAttachment,
  onRemoveAttachment,
  onPickPrompt,
  onOpenThreadHistory,
  onClose,
  hideHeader = false,
  slots,
  activePanel,
  onClosePanel,
  className,
  composerClassName,
  progress,
  renderMessageFooter,
}: AgentChatProps) {
  const isMobile = useIsMobile()
  const keyboardInset = useKeyboardInset()

  const handleSend = useCallback(
    (text: string) => {
      void onSendMessage(text, attachments)
    },
    [onSendMessage, attachments],
  )

  const handleAttach = useCallback(
    (files: File[]) => {
      void onAttachFiles(files)
    },
    [onAttachFiles],
  )

  return (
    <div
      data-slot="agent-chat"
      className={cn(
        "agent-chat-root relative flex h-full min-h-0 w-full overflow-hidden bg-background text-foreground",
        className,
      )}
    >
      {/* Side panel: on desktop, sits in its own column; on mobile,
          overlays the chat column as a full-screen sheet. */}
      {!isMobile && activePanel && (
        <SidePanelSlot
          slots={slots}
          activePanel={activePanel}
          onClose={onClosePanel}
          className="w-[60%] min-w-[320px] border-r border-black/10 dark:border-white/10"
        />
      )}

      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {!hideHeader && (
          <header className="flex h-10 shrink-0 items-center gap-1.5 border-b border-black/10 px-3 dark:border-white/10">
            <span className="font-heading text-sm">{agent.label}</span>
            <div className="flex-1" />
            {onOpenThreadHistory && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={onOpenThreadHistory}
                aria-label="Thread history"
              >
                <History />
              </Button>
            )}
            {onClose && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={onClose}
                aria-label="Close chat"
              >
                <X />
              </Button>
            )}
          </header>
        )}

        <Transcript
          agent={agent}
          messages={messages}
          status={status}
          onPickPrompt={onPickPrompt}
          progress={progress}
          renderMessageFooter={renderMessageFooter}
          className="flex-1"
        />

        <div
          className="shrink-0 px-3 pb-3"
          style={
            isMobile && keyboardInset > 0
              ? { paddingBottom: `calc(0.75rem + ${keyboardInset}px)` }
              : undefined
          }
        >
          <Composer
            agent={agent}
            agents={agents}
            status={status}
            attachments={attachments}
            onSend={handleSend}
            onStop={onStop}
            onAttach={handleAttach}
            onPickAttachment={onPickAttachment}
            onRemoveAttachment={onRemoveAttachment}
            onSelectAgent={onSelectAgent}
            className={composerClassName}
          />
        </div>

        {/* Mobile-only side panel overlays the chat column. */}
        {isMobile && activePanel && (
          <SidePanelSlot
            slots={slots}
            activePanel={activePanel}
            onClose={onClosePanel}
            isMobile
          />
        )}
      </div>
    </div>
  )
}
